
import { useState } from "react";
import type { ItemTiming } from "../types";
import { formatDate, formatTiming } from "../utils/format";
import { Button } from "./Button";
import { StatusChip } from "./StatusChip";

/**
 * One item's timing line and, on a saved plan, the control for recording
 * the date a user has actually been given.
 *
 * The declared timing (`formatTiming(timing)`) is always rendered from
 * this item's own declared fields and is never replaced by a user's own
 * date — the two stay visible side by side. `timing === null` (a pre-W4-04
 * snapshot) or `undefined` renders nothing at all: no line, no control, no
 * mention of either being unavailable. See
 * docs/design/features/W4-04.md, "UI/component delta" and "A user's own
 * date — two lines, never one".
 */
export function TimingLine({
  itemCode,
  timing,
  editable,
  onSave,
  onRemove,
}: {
  itemCode: string;
  timing: ItemTiming | null | undefined;
  /** True only on a saved plan. A preview shows the declared timing but
   * offers no deadline control, and says nothing about the difference. */
  editable: boolean;
  onSave?: (itemCode: string, deadline: string) => Promise<void>;
  onRemove?: (itemCode: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!timing) return null;

  const hintId = `deadline-hint-${itemCode}`;
  const inputId = `deadline-input-${itemCode}`;

  const startEditing = () => {
    setValue(timing.user_deadline?.date ?? "");
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    if (!onSave || !value) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(itemCode, value);
      setEditing(false);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn’t save that date. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onRemove) return;
    setBusy(true);
    setError(null);
    try {
      await onRemove(itemCode);
      setEditing(false);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn’t remove that date. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="estimate-card__timing">
      <p className="estimate-card__timing-line">{formatTiming(timing)}</p>

      {timing.user_deadline && !editing && (
        <p className="estimate-card__user-deadline">
          <StatusChip tone="neutral">Your date</StatusChip>{" "}
          {formatDate(timing.user_deadline.date)}
          {editable && onSave && onRemove && (
            <span className="estimate-card__deadline-controls">
              <Button variant="quiet" onClick={startEditing} disabled={busy}>
                Change
              </Button>
              <Button variant="quiet" onClick={() => void remove()} disabled={busy}>
                Remove
              </Button>
            </span>
          )}
        </p>
      )}

      {editable && onSave && !timing.user_deadline && !editing && (
        <>
          <Button
            variant="quiet"
            className="estimate-card__add-deadline"
            onClick={startEditing}
            aria-describedby={hintId}
          >
            Add the date on your letter
          </Button>
          <p className="field-hint" id={hintId}>
            If you’ve been given a date, we’ll use it instead of our estimate.
          </p>
        </>
      )}

      {editable && editing && (
        <form
          className="estimate-card__deadline-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label htmlFor={inputId}>Date on your letter</label>
          <input
            id={inputId}
            type="date"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            aria-describedby={`${hintId}-format`}
            required
          />
          <p className="field-hint" id={`${hintId}-format`}>
            Format: day, month, year.
          </p>
          {error && (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}
          <span className="estimate-card__deadline-actions">
            <Button type="submit" busy={busy} disabled={!value}>
              Save
            </Button>
            <Button type="button" variant="quiet" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
          </span>
        </form>
      )}
    </div>
  );
}
